import React from "react";
import "./_avatarPlayer.scss";
import AvatarFrame from "./AvatarFrame";
import EnrichedPlayerPluginEditor from "../EnrichedPlayer/components/EnrichedPlayerPluginEditor.js";
/* eslint-disable react/prop-types */

/**
 * AvatarPlayer — the presenter box of the "avatarlı zengin görsel anlatım" flow.
 *
 * Two states, one geometry:
 *   1. poster  — a still image of the avatar (lipsync_avatars.thumbnailUrl)
 *   2. clip    — an opaque H.264 MP4, written in by the batch "avatarı canlandır" run
 *
 * The frame NEVER stretches its source. It covers and crops around `focal`, so a
 * single avatar asset serves any frame aspect the layout hands it (the flow's own
 * strip is 43.75% x 100% => ~0.78, but a user may resize the box freely).
 *
 * The name matters: `classifyPluginTrackType` and `addBoxThunk` substring-match
 * /video|player|audio|sound/i, so "Player" buys correct timeline track typing and
 * media-duration endFrame derivation. Renaming this to "Avatar" silently regresses
 * both.
 */
export function AvatarPlayer(base) {
  return {
    getConfig: function () {
      return {
        name: "AvatarPlayer",
        displayName: IdealStudio.i18n.t("AvatarPlayer.PluginName"),
        category: "image",
        flavor: "react",
        icon: "person",
        needsConfigModal: false,
        needsTextEdition: false,
        isRich: false,
        // The avatar strip of a 16:9 canvas: 43.75% wide, full height.
        initialWidth: "43.75%",
        initialHeight: "100%",
        initialWidthSlide: "43.75%",
        initialHeightSlide: "100%",
      };
    },

    getInitialState: function () {
      return {
        // Identity — lipsync_avatars.id, resolved by the server when composing.
        avatarId: null,
        avatarName: "",
        // Poster still. Phase 1 renders only this.
        posterUrl: "",
        // Animated clip. Written by the batch lipsync run; null until then.
        videoUrl: null,
        // Batch job bookkeeping, so a reload resumes the spinner instead of
        // orphaning the job.
        jobId: null,
        jobStatus: "idle", // idle | queued | processing | done | failed
        // Crop focus as object-position percentages. Centred by default so the
        // still frames the presenter the same way the clip does; the author can
        // bias it from the toolbar when a particular avatar needs it.
        focalX: 50,
        focalY: 50,
        radius: 16,
        // Playback: a presenter is driven BY the scene timeline, it is not a
        // video the learner operates. autoPlay is also what sceneTimeline.es6
        // reads to decide the clip's play window (it branches on
        // `tb.state.autoPlay`); leaving it undefined made the avatar a manual
        // video that the timeline started but never played.
        autoPlay: true,
        controls: false,
        loop: false,
        // ReactPlayer scale (0–1), same as EnrichedPlayer's own default — NOT
        // the 0–100 the audio plugins use.
        volume: 1,
      };
    },

    getToolbar: function (state) {
      return {
        main: {
          __name: "Main",
          accordions: {
            avatar: {
              __name: IdealStudio.i18n.t("AvatarPlayer.avatar"),
              icon: "person",
              buttons: {
                avatarName: {
                  __name: IdealStudio.i18n.t("AvatarPlayer.avatarName"),
                  type: "text",
                  value: state.avatarName,
                  autoManaged: false,
                  readOnly: true,
                },
                posterUrl: {
                  __name: IdealStudio.i18n.t("AvatarPlayer.poster"),
                  type: "external_provider",
                  value: state.posterUrl,
                  accept: "image/*",
                  autoManaged: false,
                },
              },
            },
            framing: {
              __name: IdealStudio.i18n.t("AvatarPlayer.framing"),
              icon: "crop",
              buttons: {
                focalX: {
                  __name: IdealStudio.i18n.t("AvatarPlayer.focalX"),
                  type: "range",
                  value: state.focalX,
                  min: 0,
                  max: 100,
                  step: 1,
                  autoManaged: false,
                },
                focalY: {
                  __name: IdealStudio.i18n.t("AvatarPlayer.focalY"),
                  type: "range",
                  value: state.focalY,
                  min: 0,
                  max: 100,
                  step: 1,
                  autoManaged: false,
                },
                radius: {
                  __name: IdealStudio.i18n.t("AvatarPlayer.radius"),
                  type: "number",
                  value: state.radius,
                  min: 0,
                  max: 200,
                  step: 1,
                  autoManaged: false,
                },
              },
            },
          },
        },
        style: {
          __name: IdealStudio.i18n.t("AvatarPlayer.box_style"),
          icon: "palette",
          accordions: {
            border: {
              __name: IdealStudio.i18n.t("AvatarPlayer.border"),
              icon: "border_style",
              buttons: {
                opacity: {
                  __name: IdealStudio.i18n.t("AvatarPlayer.opacity"),
                  type: "range",
                  value: 1,
                  min: 0,
                  max: 1,
                  step: 0.01,
                },
              },
            },
          },
        },
      };
    },

    getRenderTemplate: function (state, props) {
      const hasClip = Boolean(state?.videoUrl) && state?.jobStatus === "done";

      // Same split as the visor twin: still until the batch animate lands, then
      // the real player component so the editor preview matches playback.
      if (hasClip) {
        return (
          <div
            className="avatar-player-frame avatar-player-frame--clip"
            style={{ width: "100%", height: "100%" }}
          >
            <EnrichedPlayerPluginEditor
              style={{ width: "100%", height: "100%" }}
              state={{
                ...state,
                // EnrichedPlayer reads `state.url`; the avatar keeps its clip
                // under `videoUrl` so `url` stays free for the poster.
                url: state.videoUrl,
                // Presenter defaults, read from state rather than hardcoded —
                // these used to be pinned to controls:true/autoPlay:false here
                // while the visor twin pinned the opposite, so the same box
                // played with the timeline for the learner and sat inert with
                // scrub controls for the author.
                controls: state.controls ?? false,
                autoPlay: state.autoPlay ?? true,
                loop: state.loop ?? false,
                volume: typeof state.volume === "number" ? state.volume : 1,
              }}
              props={props}
            />
          </div>
        );
      }

      return <AvatarFrame state={state} props={props} isEditor />;
    },
  };
}
